import React, { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { RegistrationContext } from '~/context/registration';
import { useToasts } from 'react-toast-notifications';
import { yupResolver } from '@hookform/resolvers/yup';
import { validationSchema } from '~/shared/validation/InsuranceValidationSchema';

import { setCampaignInsurance } from '~/services/registration';

import {
    TextInput,
    CheckboxInput,
    currencyConfig,
} from '~/shared/components/form/Inputs';
import { ProgressBar } from '../ProgressBar';
import IntlCurrencyInput from 'react-intl-currency-input';
import {
    Container,
    Button,
    Card,
    CardBody,
    Col,
    Row,
    Form,
    FormGroup,
    Input,
    Label,
} from 'reactstrap';

import { Loading } from '~/shared/components/Loading';

const insuranceOptions = [
    {
        label: 'Yes, I have insurance',
        value: 'yes',
    },
    {
        label: 'No, I do not have insurance',
        value: 'no',
    },
    {
        label: "I don't know",
        value: 'unknown',
    },
];

const yesOptions = [
    {
        label: 'I have Medicare',
        value: 'ins_has_medicare',
    },
    {
        label: 'I have Medicaid',
        value: 'ins_has_medicaid',
    },
    {
        label: 'I have Private insurance',
        value: 'ins_has_private',
    },
    {
        label: 'I have Private insurance through COBRA',
        value: 'ins_has_cobra',
    },
];

const defaultValues = {
    ins_has_medicare: false,
    ins_has_medicaid: false,
    ins_has_private: false,
    ins_has_cobra: false,
    ins_comments: '',
    ins_company: '',
    ins_premium: null,
    ins_cobra_months: null,
    ins_cobra_amount: null,
    ins_has_none: false,
    ins_unknown: false,
};

export const Insurance = ({}: any) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [{ campaignRequest }, registrationDispatch] =
        useContext(RegistrationContext);
    const [insuranceOption, setInsuranceOption] = useState<string>('');
    const [noCopay, setNoCopay] = useState<boolean>(false);
    const {
        handleSubmit,
        watch,
        control,
        reset,
        setValue,
        getValues,
        formState: { errors, isSubmitting, isValid },
    } = useForm({
        mode: 'onBlur',
        defaultValues,
        resolver: yupResolver(validationSchema),
    });

    const { addToast } = useToasts();
    const history = useHistory();

    useEffect(() => {}, []);

    const onSubmit = async (data: any) => {
        try {
            console.log('data: ', data);
            if (!data?.ins_premium) {
                data.ins_premium = 1;
            }
            const resp = await setCampaignInsurance(data, undefined);
            console.log('resp: ', resp);

            if (resp.detail) {
                if (resp.detail === 'Invalid token.') {
                    // Delete localStorage token
                    localStorage.removeItem('token');
                    history.push('/login');
                } else {
                    console.log('resp', resp);
                    addToast('There was an error saving your application', {
                        appearance: 'error',
                    });
                }
            } else {
                registrationDispatch({
                    type: 'set_application',
                    application: resp,
                });

                history.push('/get-started/community');
            }
        } catch (e) {
            console.log('e: ', e);
        }
    };

    useEffect(() => {
        if (noCopay) {
            setValue('ins_premium', null);
        }
    }, [noCopay]);

    useEffect(() => {
        if (insuranceOption === 'no') {
            reset({ ...defaultValues, ins_has_none: true });
        } else if (insuranceOption === 'unknown') {
            reset({ ...defaultValues, ins_unknown: true });
        }
    }, [insuranceOption]);

    if (loading) {
        return <Loading />;
    }

    const hasCobra = watch('ins_has_cobra');
    const hasMedicare = watch('ins_has_medicare');
    const hasMedicaid = watch('ins_has_medicaid');
    const hasPrivate = watch('ins_has_private');

    const requiresInsuranceCompany =
        hasCobra || hasMedicare || hasMedicaid || hasPrivate;

    const YesOptions = () => {
        return (
            <div>
                <Label>Check all that apply*</Label>
                <FormGroup check className='mb-3'>
                    {yesOptions.map((opt, i) => {
                        return (
                            <CheckboxInput
                                key={i}
                                control={control}
                                id={opt.value}
                                name={opt.value}
                                label={opt.label}
                                value={true}
                            />
                        );
                    })}
                </FormGroup>
                {hasCobra && (
                    <TextInput
                        name='ins_cobra_months'
                        label='Cobra Number of Months'
                        type='number'
                        control={control}
                        required={true}
                        ariaRequired={true}
                        maxLength={3}
                    />
                )}
                {hasCobra && (
                    <FormGroup>
                        <Label>Cobra Amount $*</Label>
                        <IntlCurrencyInput
                            onChange={(e, val, masked) => {
                                setValue('ins_cobra_amount', val);
                            }}
                            config={currencyConfig as any}
                            max={999999}
                            defaultValue={0}
                        />
                    </FormGroup>
                )}
                <TextInput
                    label='Comments About Insurance'
                    type='textarea'
                    control={control}
                    name='ins_comments'
                    maxLength={500}
                />
                <TextInput
                    label='Insurance Company'
                    control={control}
                    name='ins_company'
                    required={requiresInsuranceCompany}
                    maxLength={100}
                />
                <div>
                    <Label check>
                        <Input
                            type='checkbox'
                            checked={noCopay}
                            onChange={() => {
                                setNoCopay(!noCopay);
                            }}
                            name='no_copay'
                        />
                        I do not have a Premium or Copay
                    </Label>
                </div>
                {!noCopay && (
                    <FormGroup>
                        <Label>
                            Insurance Premium $ (Your monthly or annual
                            insurance payment)*
                        </Label>
                        <IntlCurrencyInput
                            className='form-control w-50'
                            onChange={(e, val, masked) => {
                                setValue('ins_premium', val);
                            }}
                            config={currencyConfig as any}
                            max={999999}
                            defaultValue={0}
                        />
                    </FormGroup>
                )}
            </div>
        );
    };

    const errorCount = Object.keys(errors).length;

    return (
        <Container>
            <ProgressBar selectedStep='financials' />

            <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className='justify-content-center py-5'>
                    <Col md='6'>
                        <div className='text-center'>
                            <h3>
                                Do you have any insurance that can help cover
                                some of the costs?
                            </h3>
                        </div>

                        <Card
                            className='box-shadow my-5'
                            style={{
                                maxWidth: '400px',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                            }}
                        >
                            <CardBody>
                                <FormGroup check className='mb-3'>
                                    {insuranceOptions.map((opt, i) => {
                                        return (
                                            <div key={i}>
                                                <Label check>
                                                    <Input
                                                        type='radio'
                                                        value={opt.value}
                                                        onClick={() =>
                                                            setInsuranceOption(
                                                                opt.value
                                                            )
                                                        }
                                                        name='insurance_option'
                                                    />
                                                    {opt.label}
                                                </Label>
                                                {opt.value == 'yes' &&
                                                    insuranceOption ==
                                                        'yes' && <YesOptions />}
                                            </div>
                                        );
                                    })}
                                </FormGroup>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <div className='text-center mb-5'>
                    <Button
                        color='outline-dark'
                        disabled={isSubmitting}
                        onClick={() =>
                            history.push('/get-started/financials/contribution')
                        }
                    >
                        Back
                    </Button>
                    <Button
                        color='primary'
                        type='submit'
                        disabled={
                            isSubmitting || !insuranceOption || errorCount > 0
                        }
                    >
                        {isSubmitting ? `Saving...` : `Next`}
                    </Button>

                    <div className='mt-4'>
                        <Button color='link'>Skip for Now</Button>
                        <p>
                            You will be required to provide this information in
                            order to receive any funds raised for this campaign.
                        </p>
                    </div>
                </div>
            </Form>
        </Container>
    );
};
